/**
 * CPSC DISCLAIM Processing Type Validator
 *
 * Validates DISCLAIM submissions per CATAIR PG01:
 * - disclaimerCode required (A or B)
 * - intendedUseDetails.description conditional (required if code = "980.000")
 */
import type { AppRecordValidateResult, NamePath } from '@xbcb/ui-types';
import { CpscProcessingType } from '__generated__/graphql';
import { PgaValidator } from 'libs/entryValidation/pga/types';
import type { CpscPga, CpscValidationArgs } from './types';
import {
  createValidationResult,
  mergeValidationResults,
  validateIntendedUse,
} from './cpscCommonValidation';

/**
 * CPSC DISCLAIM validator
 */
export class CpscDisclaimValidator implements PgaValidator<CpscPga> {
  /**
   * Check if this validator applies to the given PGA data
   * @return {boolean} True if processing type is DISCLAIM
   */
  isApplicable({ pga }: CpscValidationArgs): boolean {
    return pga?.processingType === CpscProcessingType.DISCLAIM;
  }

  /**
   * Validate DISCLAIM processing type fields
   * @return {AppRecordValidateResult} Validation result
   */
  validate({
    pga,
    tariffNamePath,
    pgaFlag,
  }: CpscValidationArgs): AppRecordValidateResult {
    const cpscPath: NamePath = [...tariffNamePath, 'pga', pgaFlag];
    const result = createValidationResult();

    // Validate disclaimerCode required
    if (!pga?.disclaimerCode) {
      result.validateFields.push([...cpscPath, 'disclaimerCode']);
    }

    // Validate intendedUseDetails (code required, description conditional)
    const intendedUseResult = validateIntendedUse(pga, cpscPath);

    return mergeValidationResults(result, intendedUseResult);
  }
}
